// Vendor Imports
import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { isEqual, get } from 'lodash';

import { ForgeButton, ForgeIcon, ForgeIconButton, ForgeSwitch } from '@tylertech/forge-react';
import { VifAuthoring, AuthoringWorkflowState } from 'common/authoring_workflow/reducers/types';
import { FormatStyle, FontStyle, FontStyleTypes } from 'common/visualizations/views/agGridReact/types';
import {
  updateTableVisualizationHeaderStyle,
  updateTableVisualizationHeaderTextStyle,
} from 'common/authoring_workflow/actions';
import { COLORS, presentationPaneScope } from 'common/authoring_workflow/constants';
import ColorPickerWithLabel from 'common/components/ColorPickerWithLabel';
import { I18n } from 'common/i18n';
import { getHeaderFormat } from 'common/authoring_workflow/selectors/vifAuthoring';
import {
  DEFAULT_TABLE_HEADER_TEXT_COLOR,
  DEFAULT_TABLE_HEADER_BACKGROUND_COLOR,
} from 'common/visualizations/views/agGridReact/Constants';
import './AgGridHeaderFormatSelector.scss';
import { isTableHeaderWrapEnabled } from 'common/visualizations/helpers/VifSelectors';

export interface AgGridHeaderFormatSelectorProps {
  vifAuthoring: VifAuthoring;
  onUpdateHeaderFormatStyle: (newHeaderFormat: FormatStyle) => void;
  onUpdateHeaderFormatTextStyle: (newHeaderTextFormat: FontStyle) => void;
}

const AgGridHeaderFormatSelector = (props: AgGridHeaderFormatSelectorProps): JSX.Element => {
  const { vifAuthoring, onUpdateHeaderFormatStyle, onUpdateHeaderFormatTextStyle } = props;
  const textColorLabel = I18n.t('common.schema_preview.data_types.text');
  const backgroundColorLabel = I18n.t('fields.row_stripe.fill_label', { scope: presentationPaneScope });
  const headerFormat: FormatStyle = getHeaderFormat(vifAuthoring);
  const textColor = headerFormat.textColor || DEFAULT_TABLE_HEADER_TEXT_COLOR;
  const backgroundColor = headerFormat.backgroundColor || DEFAULT_TABLE_HEADER_BACKGROUND_COLOR;
  const fontStyle = useMemo(() => get(headerFormat, 'fontStyle'), [headerFormat]);
  const defaultFontStyle: FontStyle = {
    isBold: true,
    isItalic: false,
    isWrapped: false,
  };
  const fontStyleIsBold = fontStyle?.isBold === undefined ? defaultFontStyle.isBold : fontStyle.isBold;
  const fontStyleIsItalic =
    fontStyle?.isItalic === undefined ? defaultFontStyle.isItalic : fontStyle.isItalic;

  const defaultHeaderFormat = {
    textColor: DEFAULT_TABLE_HEADER_TEXT_COLOR,
    backgroundColor: DEFAULT_TABLE_HEADER_BACKGROUND_COLOR,
    fontStyle: defaultFontStyle,
    textFont: '',
    ...(isTableHeaderWrapEnabled() && {
      wrapHeaders: true,
    }),
  };

  const resetButtonLabel = I18n.t('fields.column_format.reset_to_default', { scope: presentationPaneScope });

  const handleColorChange = (key: string, newColor: string) => {
    onUpdateHeaderFormatStyle({
      ...(headerFormat ? headerFormat : defaultHeaderFormat),
      [key]: newColor,
    });
  };

  const handleTextStyleChange = (textStyle: FontStyleTypes.isBold | FontStyleTypes.isItalic) => {
    const currentFontStyle = { isBold: fontStyleIsBold, isItalic: fontStyleIsItalic };
    onUpdateHeaderFormatTextStyle({
      ...(fontStyle ? fontStyle : defaultFontStyle),
      [textStyle]: !currentFontStyle[textStyle],
    });
  };

  const handleResetHeaderFormat = () => {
    onUpdateHeaderFormatStyle(defaultHeaderFormat);
  };

  const isHeaderFormatEqual = (selectedFormat: FormatStyle) => {
    const selectedHeaderFormat = {
      textColor: selectedFormat.textColor || DEFAULT_TABLE_HEADER_TEXT_COLOR,
      backgroundColor: selectedFormat.backgroundColor || DEFAULT_TABLE_HEADER_BACKGROUND_COLOR,
      fontStyle: {
        isBold: selectedFormat?.fontStyle?.isBold === undefined ? true : selectedFormat.fontStyle.isBold,
        isItalic:
          selectedFormat?.fontStyle?.isItalic === undefined ? false : selectedFormat.fontStyle.isItalic,
        isWrapped: false,
      },
      textFont: '',
      ...(isTableHeaderWrapEnabled() && {
        wrapHeaders: headerFormat?.wrapHeaders ?? false,
      }),
    };
    return isEqual(selectedHeaderFormat, defaultHeaderFormat);
  };

  const handleHeaderWrap = (event: CustomEvent) => {
    const updatedFormat = {
      ...(headerFormat ? headerFormat : defaultHeaderFormat),
      ...(isTableHeaderWrapEnabled() && {
        wrapHeaders: Boolean(event.detail),
      }),
    };
    onUpdateHeaderFormatStyle(updatedFormat);
  };

  return (
    <div className="ag-grid-header-format-container">
      <div className="text-formatting">
        <div className="header-color-selector">
          <ColorPickerWithLabel
            keyId="text"
            color={textColor}
            onChangeColor={(newColor: string) => {
              handleColorChange('textColor', newColor);
            }}
            label={textColorLabel}
            palette={COLORS}
            uniqueId="headers-text"
          />
          <ColorPickerWithLabel
            keyId="fill"
            color={backgroundColor}
            onChangeColor={(newColor: string) => {
              handleColorChange('backgroundColor', newColor);
            }}
            label={backgroundColorLabel}
            palette={COLORS}
            uniqueId="headers-fill"
          />
        </div>
        <ForgeIconButton
          toggle
          on={fontStyleIsBold}
          aria-label="bold"
          onClick={() => handleTextStyleChange(FontStyleTypes.isBold)}
        >
          <ForgeIcon name="format_bold" slot="on" />
          <ForgeIcon name="format_bold" />
        </ForgeIconButton>
        <ForgeIconButton
          toggle
          on={fontStyleIsItalic}
          aria-label="italic"
          onClick={() => handleTextStyleChange(FontStyleTypes.isItalic)}
        >
          <ForgeIcon name="format_italic" slot="on" />
          <ForgeIcon name="format_italic" />
        </ForgeIconButton>
      </div>
      {isTableHeaderWrapEnabled() && (
        <div className="wrap-headers-container">
          <ForgeSwitch
            checked={headerFormat.wrapHeaders ?? false}
            on-forge-switch-change={handleHeaderWrap}
            data-testid="wrap-headers-switch"
          >
            <span className="forge-typography--label3">
              {I18n.t('shared.visualizations.panes.presentation.fields.wrap_headers')}
            </span>
          </ForgeSwitch>
        </div>
      )}
      <ForgeButton
        className="ag-grid-reset-format-button"
        aria-label={resetButtonLabel}
        onClick={handleResetHeaderFormat}
        disabled={isHeaderFormatEqual(headerFormat)}
      >
        <ForgeIcon name="refresh" />
        <span>{resetButtonLabel}</span>
      </ForgeButton>
    </div>
  );
};

const mapDispatchToProps = {
  onUpdateHeaderFormatStyle: updateTableVisualizationHeaderStyle,
  onUpdateHeaderFormatTextStyle: updateTableVisualizationHeaderTextStyle,
};

const mapStateToProps = (state: AuthoringWorkflowState) => ({
  vifAuthoring: state.vifAuthoring,
});

export default connect(mapStateToProps, mapDispatchToProps)(AgGridHeaderFormatSelector);
